// Source: https://github.com/ueberdosis/tiptap/issues/333
import { NodeViewWrapper, NodeViewProps, ReactNodeViewRenderer } from '@tiptap/react';
import { TextField } from '@scale/scaleui-radix';
import TipTapImage from '@tiptap/extension-image';
import { CSSProperties, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const MIN_WIDTH = 60;
const BOX_COLOR = '#0096fd';

const useEvent = <T extends (...args: any[]) => any>(handler: T): T => {
  const handlerRef = useRef<T | null>(null);

  useLayoutEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  return useCallback((...args: Parameters<T>): ReturnType<T> => {
    if (!handlerRef.current) {
      throw new Error('Handler is not assigned');
    }
    return handlerRef.current(...args);
  }, []) as T;
};

const ResizableImageTemplate = ({ node, updateAttributes }: NodeViewProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [editing, setEditing] = useState(false);
  const [resizingStyle, setResizingStyle] = useState<Pick<CSSProperties, 'width'> | undefined>();
  const [altText, setAltText] = useState(node.attrs.alt || '');

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setEditing(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleMouseDown = useEvent((event: React.MouseEvent<HTMLDivElement>) => {
    if (!imgRef.current) return;
    event.preventDefault();

    const direction = event.currentTarget.dataset.direction || '--';
    const initialXPosition = event.clientX;
    const currentWidth = imgRef.current.width;
    let newWidth = currentWidth;
    const transform = direction[1] === 'w' ? -1 : 1;

    const removeListeners = () => {
      window.removeEventListener('mousemove', mouseMoveHandler);
      window.removeEventListener('mouseup', removeListeners);
      updateAttributes({ width: newWidth });
      setResizingStyle(undefined);
    };

    const mouseMoveHandler = (event: MouseEvent) => {
      newWidth = Math.max(currentWidth + transform * (event.clientX - initialXPosition), MIN_WIDTH);
      setResizingStyle({ width: newWidth });
      if (!event.buttons) removeListeners();
    };

    window.addEventListener('mousemove', mouseMoveHandler);
    window.addEventListener('mouseup', removeListeners);
  });

  const dragCornerButton = (direction: string) => (
    <div
      role="button"
      tabIndex={0}
      onMouseDown={handleMouseDown}
      data-direction={direction}
      style={{
        position: 'absolute',
        height: '10px',
        width: '10px',
        backgroundColor: BOX_COLOR,
        ...{ n: { top: 0 }, s: { bottom: 0 } }[direction[0]],
        ...{ w: { left: 0 }, e: { right: 0 } }[direction[1]],
        cursor: `${direction}-resize`,
      }}
    />
  );

  const handleAltChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newAltText = event.target.value;
    setAltText(newAltText);
    updateAttributes({ alt: newAltText });
  };

  return (
    <NodeViewWrapper
      ref={containerRef}
      as="div"
      draggable
      data-drag-handle
      onClick={() => setEditing(true)}
      onBlur={() => setEditing(false)}
    >
      <div
        style={{
          overflow: 'hidden',
          position: 'relative',
          display: 'inline-block',
          lineHeight: '0px',
        }}
      >
        <img {...node.attrs} ref={imgRef} style={{ ...resizingStyle, cursor: 'default' }} />
        {editing && (
          <>
            <TextField.Root
              size="1"
              value={altText}
              onChange={handleAltChange}
              placeholder="Enter alt text..."
            />
            {[
              { left: 0, top: 0, height: '100%', width: '1px' },
              { right: 0, top: 0, height: '100%', width: '1px' },
              { top: 0, left: 0, width: '100%', height: '1px' },
              { bottom: 0, left: 0, width: '100%', height: '1px' },
            ].map((style, i) => (
              <div key={i} style={{ position: 'absolute', backgroundColor: BOX_COLOR, ...style }} />
            ))}
            {dragCornerButton('nw')}
            {dragCornerButton('ne')}
            {dragCornerButton('sw')}
            {dragCornerButton('se')}
          </>
        )}
      </div>
    </NodeViewWrapper>
  );
};

const ResizableImage = TipTapImage.extend({
  name: 'resizableImage',

  addAttributes() {
    return {
      ...this.parent?.(),
      src: { default: null },
      alt: { default: null },
      width: {
        default: null,
        parseHTML: element => element.getAttribute('width'),
        renderHTML: attributes => (attributes.width ? { width: attributes.width } : {}),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'img[src]',
        getAttrs: dom => {
          if (typeof dom === 'string') return {};
          const element = dom as HTMLElement;
          return {
            src: element.getAttribute('src'),
            alt: element.getAttribute('alt'),
            width: element.getAttribute('width'),
          };
        },
      },
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageTemplate);
  },
});

export default ResizableImage.configure({ inline: true });
